import { EventType, Jollywhere } from '@/constants';
import { formatMoney } from '@/helpers/common';
import type { BetItem } from '@/models/bet';
import type { BetFailItem } from '@/models/betFail';
import i18n from '@/plugins/i18n';
import { currencyDictionary } from '@/stores/simpleDictionary/currency';
import { eventPeriodDictionary } from '@/stores/simpleDictionary/eventPeriod';

const t = i18n.global.t;

export function useBetFormatters() {
    const getCurrencySymbol = (userCurrencyId: number) => currencyDictionary.itemById(userCurrencyId).symbol;

    const formatMoneyCurrency = (value: string | number, userCurrencyId: number, precision = 2) =>
        `${getCurrencySymbol(userCurrencyId)}${formatMoney(value, precision)}`;

    const formatMoneyCurrencyWithSign = (value: string | null, userCurrencyId: number, precision = 2) =>
        value ? `${getCurrencySymbol(userCurrencyId)}${+value > 0 ? '+' : ''}${formatMoney(value, precision)}` : '';

    const getPeriodDisplay = (item: BetItem | BetFailItem) => eventPeriodDictionary.itemById(item.periodTypeId).code;

    const getValueClass = (value?: string | number | null) => {
        if (value === null || value === undefined || value === '') return '';
        const numeric = +value;
        if (numeric > 0) return 'text-tbGreen';
        if (numeric < 0) return 'text-tbRed';
        return 'text-tbYellow';
    };

    const formatLiveMinute = (item: BetItem | BetFailItem) => {
        if (item.eventType === EventType.Pregame) {
            return '';
        }

        if (item.eventType === EventType.Live && !item.time && item.timeMin == null) {
            return 'N/C';
        }

        if (item.time === 'HT' || item.time === 'Live') {
            return item.time;
        }

        const value = item.timeMin ?? 0;
        const period = item.timeH;

        if (period === 2) {
            const minute = 45 + value;
            return (minute <= 90 ? minute : `90+${minute - 90}`) + "'";
        }

        return (value <= 45 ? value : `45+${value - 45}`) + "'";
    };

    const formatPercent = (value?: string | number | null) => (value !== null && value !== undefined ? `${value}%` : '');

    const formatPair = (home: number | null, away: number | null, suffix = '') => {
        if (home === null && away === null) return null;
        if (home === null) return `-${away}${suffix}`;
        if (away === null) return `${home}${suffix}-`;
        return `${home}${suffix}-${away}${suffix}`;
    };

    const formatCorners = (home: number | null, away: number | null) => {
        if (home === null && away === null) return '';
        return formatPair(home, away) || '';
    };

    const formatExchangeRate = (value?: string | null) => {
        if (!value) return '';
        const num = +value;
        if (Number.isNaN(num)) return value;
        return num.toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
    };

    const getResultClass = (result: string | null) => {
        if (result === 'W' || result === 'HW') return 'text-tbGreen';
        if (result === 'L' || result === 'HL') return 'text-tbRed';
        if (result === 'P') return 'text-tbYellow';
        return '';
    };

    const getExchangeLabel = (item: BetItem | BetFailItem) => {
        const playerCurrencySymbol = getCurrencySymbol(item.userCurrencyId);
        const accountCurrencySymbol = getCurrencySymbol(item.accountCurrencyId);
        return `${playerCurrencySymbol} ⇒ ${accountCurrencySymbol}`;
    };

    const getTeamColorClass = (item: BetItem | BetFailItem, isHome: boolean) => {
        if (item.jollywhere === Jollywhere.Home) return isHome ? 'text-tbRed' : 'text-blue';
        if (item.jollywhere === Jollywhere.Away) return isHome ? 'text-blue' : 'text-tbRed';
        return '';
    };

    const formatMarket = (item: BetItem | BetFailItem) => {
        const marketType = item.eventType === EventType.Live ? t('common.live') : t('common.marketDballShort');
        const period = getPeriodDisplay(item);
        return `${marketType} | ${period}`;
    };

    const formatRange = (from: string | null, to: string | null, suffix = '') => {
        if (from === null) return `to ${to}${suffix}`;
        if (to === null) return `from ${from}${suffix}`;
        return `${from}${suffix} to ${to}${suffix}`;
    };

    return {
        getCurrencySymbol,
        formatMoneyCurrency,
        formatMoneyCurrencyWithSign,
        getPeriodDisplay,
        getValueClass,
        formatLiveMinute,
        formatPercent,
        formatPair,
        formatCorners,
        formatExchangeRate,
        getResultClass,
        getExchangeLabel,
        getTeamColorClass,
        formatMarket,
        formatRange,
    };
}
